import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ReportsLibrary = ({ reports, onExportReport, onPreviewReport, onDeleteReport }) => {
  const { t } = useTranslation();
  const [filterType, setFilterType] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [sortBy, setSortBy] = useState('created_desc');

  const reportTypes = [
    { value: '', label: 'All Report Types' },
    { value: 'eu-directive-standard', label: 'EU Directive Standard' },
    { value: 'germany-entgelttransparenz', label: 'Germany Compliance' },
    { value: 'france-index-egalite', label: 'France Compliance' },
    { value: 'uk-gender-pay-gap', label: 'UK Compliance' },
    { value: 'custom-template', label: 'Custom Templates' }
  ];

  const periodFilters = [
    { value: '', label: 'All Periods' },
    { value: '2024', label: '2024 Reports' },
    { value: '2023', label: '2023 Reports' },
    { value: 'q4-2024', label: 'Q4 2024' },
    { value: 'q3-2024', label: 'Q3 2024' }
  ];

  const sortOptions = [
    { value: 'created_desc', label: 'Newest First' },
    { value: 'created_asc', label: 'Oldest First' },
    { value: 'name_asc', label: 'Name A-Z' },
    { value: 'type_asc', label: 'Type A-Z' },
    { value: 'period_desc', label: 'Period (Latest)' }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'generated': { color: 'bg-success text-success-foreground', icon: 'CheckCircle' },
      'processing': { color: 'bg-warning text-warning-foreground', icon: 'Clock' },
      'error': { color: 'bg-destructive text-destructive-foreground', icon: 'AlertCircle' },
      'draft': { color: 'bg-secondary text-secondary-foreground', icon: 'Edit' }
    };

    const config = statusConfig?.[status] || statusConfig?.draft;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const filteredReports = reports?.filter(report => {
    const typeMatch = !filterType || report?.template === filterType;
    const periodMatch = !filterPeriod || report?.period?.includes(filterPeriod);
    return typeMatch && periodMatch;
  });

  const sortedReports = [...filteredReports]?.sort((a, b) => {
    switch (sortBy) {
      case 'created_desc':
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 'created_asc':
        return new Date(a.createdAt) - new Date(b.createdAt);
      case 'name_asc':
        return a?.name?.localeCompare(b?.name);
      case 'type_asc':
        return a?.template?.localeCompare(b?.template);
      case 'period_desc':
        return b?.period?.localeCompare(a?.period);
      default:
        return 0;
    }
  });

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Archive" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Reports Library</h2>
              <p className="text-sm text-muted-foreground">{reports?.length} compliance reports generated</p>
            </div>
          </div>
          <Button variant="outline" iconName="Download" iconPosition="left">
            Bulk Export
          </Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label={t('complianceReportsPage.library.filterByType')}
            options={reportTypes}
            value={filterType}
            onChange={setFilterType}
            placeholder={t('complianceReportsPage.library.allTypes')}
          />
          <Select
            label={t('complianceReportsPage.library.filterByPeriod')}
            options={periodFilters}
            value={filterPeriod}
            onChange={setFilterPeriod}
            placeholder={t('complianceReportsPage.library.allPeriods')}
          />
          <Select
            label={t('complianceReportsPage.library.sortBy')}
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
          />
        </div>
      </div>
      {/* Reports List */}
      <div className="divide-y divide-border">
        {sortedReports?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Reports Found</h3>
            <p className="text-muted-foreground">No reports match your current filters. Try adjusting the filter criteria.</p>
          </div>
        ) : (
          sortedReports?.map((report) => (
            <div key={report?.id} className="p-6 hover:bg-muted/50 transition-smooth">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-medium text-foreground">{report?.name}</h3>
                    {getStatusBadge(report?.status)}
                    {report?.isImmutable && (
                      <span className="inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        <Icon name="Lock" size={12} />
                        <span>Immutable</span>
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-muted-foreground mb-3">
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={14} />
                      <span>{report?.period}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Building" size={14} />
                      <span>{report?.scope}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="FileText" size={14} />
                      <span>{formatFileSize(report?.fileSize)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Clock" size={14} />
                      <span>{new Date(report.createdAt)?.toLocaleDateString()}</span>
                    </div>
                  </div>

                  {report?.checksum && (
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <Icon name="Shield" size={12} />
                      <span>Checksum: {report?.checksum}</span>
                      <span className="text-success">✓ Verified</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={() => onPreviewReport(report)}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Download"
                    onClick={() => onExportReport(report)}
                  >
                    Export
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="MoreVertical"
                    onClick={() => {/* Show more options */}}
                  />
                </div>
              </div>

              {/* Export Formats */}
              {report?.availableFormats && (
                <div className="mt-3 flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground">Available formats:</span>
                  {report?.availableFormats?.map((format) => (
                    <span
                      key={format}
                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-muted text-muted-foreground"
                    >
                      {format?.toUpperCase()}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {/* Footer */}
      {sortedReports?.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Showing {sortedReports?.length} of {reports?.length} reports</span>
            <div className="flex items-center space-x-2">
              <Icon name="Info" size={14} />
              <span>All reports include audit trail and GDPR compliance</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsLibrary;